import { DomainError, type DomainErrorFields } from "./errors";

/** Limites da grade (RF-CAD-02, aba Variação). */
export const MAX_GRID_ATTRIBUTES = 5;
export const MAX_GRID_OPTIONS = 50;
/** Variações (filhos) por produto pai, contando as que já existem. */
export const MAX_VARIATIONS = 200;
export const MAX_ATTRIBUTE_NAME = 30;
export const MAX_OPTION_LENGTH = 40;
const MAX_SKU = 60;
const MAX_NAME = 120;

export type GridAttribute = { name: string; options: string[] };
export type VariationValue = { attribute: string; value: string };
export type GridIssue = { path: (string | number)[]; message: string };

const clean = (s: string) => s.replace(/\s+/g, " ").trim();
/** Forma de comparação: sem acento, sem caixa e com espaços normalizados ("Cór" = "cor"). */
const fold = (s: string) => clean(s).normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

const tidy = (attrs: GridAttribute[]): GridAttribute[] =>
  attrs.map((a) => ({ name: clean(a.name), options: a.options.map(clean).filter((o) => o !== "") }));

/** Problemas da grade já arrumada (para o Zod e para o DO), com o caminho do campo. */
export function gridIssues(raw: GridAttribute[]): GridIssue[] {
  const attrs = tidy(raw);
  const issues: GridIssue[] = [];
  if (attrs.length === 0) return [{ path: [], message: "Informe ao menos um atributo" }];
  if (attrs.length > MAX_GRID_ATTRIBUTES)
    return [{ path: [], message: `No máximo ${MAX_GRID_ATTRIBUTES} atributos` }];
  const names = new Set<string>();
  attrs.forEach((a, i) => {
    if (!a.name) issues.push({ path: [i, "name"], message: "Informe o nome do atributo" });
    else if (a.name.length > MAX_ATTRIBUTE_NAME)
      issues.push({
        path: [i, "name"],
        message: `Nome do atributo com até ${MAX_ATTRIBUTE_NAME} caracteres`,
      });
    else if (names.has(fold(a.name)))
      issues.push({ path: [i, "name"], message: `Atributo repetido: ${a.name}` });
    names.add(fold(a.name));

    if (a.options.length === 0) issues.push({ path: [i, "options"], message: "Informe ao menos uma opção" });
    else if (a.options.length > MAX_GRID_OPTIONS)
      issues.push({ path: [i, "options"], message: `No máximo ${MAX_GRID_OPTIONS} opções por atributo` });
    else {
      const seen = new Set<string>();
      for (const o of a.options) {
        if (o.length > MAX_OPTION_LENGTH) {
          issues.push({
            path: [i, "options"],
            message: `Opção com até ${MAX_OPTION_LENGTH} caracteres: ${o}`,
          });
          break;
        }
        if (seen.has(fold(o))) {
          issues.push({ path: [i, "options"], message: `Opção repetida: ${o}` });
          break;
        }
        seen.add(fold(o));
      }
    }
  });
  if (issues.length) return issues;
  const total = attrs.reduce((n, a) => n * a.options.length, 1);
  if (total > MAX_VARIATIONS)
    return [{ path: [], message: `A grade passa de ${MAX_VARIATIONS} combinações (${total})` }];
  return [];
}

/** Grade arrumada (espaços, opções vazias fora) ou DomainError `invalid_grid` com os campos. */
export function normalizeGrid(raw: GridAttribute[]): GridAttribute[] {
  const issues = gridIssues(raw);
  if (issues.length) {
    const fields: DomainErrorFields = {};
    for (const i of issues) fields[i.path.join(".")] ??= i.message;
    throw new DomainError("invalid_grid", issues[0]!.message, fields);
  }
  return tidy(raw);
}

/** Produto cartesiano das opções; o primeiro atributo varia mais devagar (ordem da tela). */
export function gridCombinations(attrs: GridAttribute[]): VariationValue[][] {
  let out: VariationValue[][] = [[]];
  for (const a of attrs)
    out = out.flatMap((prefix) => a.options.map((value) => [...prefix, { attribute: a.name, value }]));
  return attrs.length ? out : [];
}

/** Identidade da combinação: independe da ordem dos atributos, da caixa e dos acentos. */
export function variationKey(values: VariationValue[]): string {
  return values
    .map((v) => `${fold(v.attribute)}=${fold(v.value)}`)
    .sort()
    .join("|");
}

/** "Camiseta Básica - Azul / P"; o nome do pai é cortado para caber em 120 caracteres. */
export function variationName(parentName: string, values: VariationValue[]): string {
  const suffix = ` - ${values.map((v) => v.value).join(" / ")}`;
  if (suffix.length >= MAX_NAME) return suffix.slice(3, MAX_NAME + 3);
  return `${parentName.trim().slice(0, MAX_NAME - suffix.length)}${suffix}`;
}

const slug = (s: string) =>
  s
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "") || "V";

/**
 * SKU do filho: SKU do pai + valores em ASCII maiúsculo ("CAM-01-AZUL-P"), até 60 caracteres. Ocupado, ganha "-2",
 * "-3"… Sem SKU no pai, o filho fica sem SKU.
 */
export function variationSku(
  parentSku: string | null,
  values: VariationValue[],
  isTaken: (sku: string) => boolean,
): string | null {
  if (!parentSku) return null;
  const base = [parentSku.trim().toUpperCase(), ...values.map((v) => slug(v.value))]
    .join("-")
    .slice(0, MAX_SKU);
  if (!isTaken(base)) return base;
  for (let n = 2; n <= MAX_VARIATIONS + 1; n++) {
    const suffix = `-${n}`;
    const candidate = `${base.slice(0, MAX_SKU - suffix.length)}${suffix}`;
    if (!isTaken(candidate)) return candidate;
  }
  throw new DomainError("duplicate_sku", `Não há SKU livre para a variação ${base}.`);
}
